/**
 * Quick Add Soul — pre-cart variant + subscription-plan selection popup.
 * Triggered by [data-quick-add-soul="<productId>"] buttons on product cards.
 * On successful add-to-cart, hands off to the cart-added-popup custom element.
 */
(function () {
  "use strict";

  /* ─── Money formatting (mirrors cart-added-popup.js) ─── */
  function formatMoney(cents) {
    if (typeof cents !== "number") return "";
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    return new Intl.NumberFormat(
      (window.Shopify && window.Shopify.locale) || "en",
      {
        style: "currency",
        currency:
          (window.Shopify &&
            window.Shopify.currency &&
            window.Shopify.currency.active) ||
          "USD",
      },
    ).format(cents / 100);
  }

  /* ─── Custom element ─── */
  class QuickAddSoul extends HTMLElement {
    connectedCallback() {
      this._productId = this.dataset.productId;
      this._dialog = this.querySelector(".soul-quick-add__dialog");
      this._overlay = this.querySelector("[data-quick-add-overlay]");
      this._form = this.querySelector(".soul-quick-add__form");
      this._variantInput = this.querySelector("[data-quick-add-variant-input]");
      this._priceEl = this.querySelector("[data-quick-add-price]");
      this._priceSaleEl = this.querySelector("[data-quick-add-price-sale]");
      this._priceCompareEl = this.querySelector(
        "[data-quick-add-price-compare]",
      );
      this._atcBtn = this.querySelector("[data-quick-add-atc]");
      this._atcLabel = this.querySelector("[data-quick-add-atc-label]");
      this._atcSpinner = this.querySelector(".soul-quick-add__atc-spinner");
      this._errorEl = this.querySelector("[data-quick-add-error]");
      this._sellingPlanSelect = this.querySelector(
        "[data-quick-add-selling-plan-select]",
      );
      this._subscriptionDetails = this.querySelector(
        "[data-quick-add-subscription-details]",
      );
      // Parse embedded product JSON
      const jsonEl = this.querySelector("[data-quick-add-product-json]");
      this._product = jsonEl ? JSON.parse(jsonEl.textContent) : null;

      // Seed subscription state from whichever radio is checked in the markup
      const checkedRadio = this.querySelector(
        "[data-quick-add-plan-radio]:checked",
      );
      this._isSubscribed = checkedRadio
        ? checkedRadio.value === "subscribe"
        : false;

      // Seed current price from the initially selected/first available variant
      this._currentPrice = (function (product, variantInput) {
        if (!product || !product.variants) return null;
        var id = variantInput ? parseInt(variantInput.value, 10) : null;
        var v =
          id &&
          product.variants.find(function (x) {
            return x.id === id;
          });
        return (
          (v && v.price) ||
          (product.variants[0] && product.variants[0].price) ||
          null
        );
      })(this._product, this._variantInput);

      // Track currently selected options
      this._selectedOptions = this._getInitialOptions();

      this._bindEvents();

      // Render correct price straight away (subscribe is pre-checked in Liquid)
      this._updatePriceDisplay();
    }

    /* ── Determine starting option values from the selected <select> options ── */
    _getInitialOptions() {
      const options = [];
      this.querySelectorAll("[data-option-select]").forEach(function (sel) {
        options[parseInt(sel.dataset.optionIndex, 10)] = sel.value || "";
      });
      return options;
    }

    /* ── Event binding ── */
    _bindEvents() {
      const self = this;

      // Define ESC handler early so open() can always reference it
      this._keydownHandler = function (e) {
        if (e.key === "Escape") self.close();
      };

      // Close via overlay click
      if (this._overlay) {
        this._overlay.addEventListener("click", function () {
          self.close();
        });
      }

      // Close via [data-quick-add-close] button
      this.querySelectorAll("[data-quick-add-close]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          self.close();
        });
      });

      // Option select changes
      this.querySelectorAll("[data-option-select]").forEach(function (sel) {
        sel.addEventListener("change", function () {
          self._onOptionSelectChange(sel);
        });
      });

      // Subscription radio toggles
      this.querySelectorAll("[data-quick-add-plan-radio]").forEach(
        function (radio) {
          radio.addEventListener("change", function () {
            self._onPlanRadioChange(radio);
          });
        },
      );

      // Form submit
      if (this._form) {
        this._form.addEventListener("submit", function (e) {
          e.preventDefault();
          self._onFormSubmit();
        });
      }
    }

    /* ── Option select change ── */
    _onOptionSelectChange(sel) {
      const optionIndex = parseInt(sel.dataset.optionIndex, 10);
      this._selectedOptions[optionIndex] = sel.value;
      this._resolveVariant();
    }

    /* ── Find matching variant from embedded JSON ── */
    _resolveVariant() {
      if (!this._product || !this._product.variants) return;

      const self = this;
      const product = this._product;
      const matched = product.variants.find(function (v) {
        return v.options.every(function (optVal, i) {
          return optVal === self._selectedOptions[i];
        });
      });

      if (!matched) return;

      // Update hidden id input
      if (this._variantInput) {
        this._variantInput.value = matched.id;
      }

      // Update displayed price
      this._currentPrice = matched.price;
      this._updatePriceDisplay();

      // Update button availability
      if (this._atcBtn && this._atcLabel) {
        if (matched.available) {
          this._atcBtn.disabled = false;
          this._atcLabel.textContent =
            (window.variantStrings && window.variantStrings.addToCart) ||
            "Add to cart";
        } else {
          this._atcBtn.disabled = true;
          this._atcLabel.textContent =
            (window.variantStrings && window.variantStrings.soldOut) ||
            "Sold out";
        }
      }

      // Mark unavailable pills
      this._updateUnavailablePills();
    }

    /* ── Mark unavailable options in selects ── */
    _updateUnavailablePills() {
      if (!this._product || !this._product.variants) return;
      const self = this;

      this.querySelectorAll("[data-option-select]").forEach(function (sel) {
        const groupIndex = parseInt(sel.dataset.optionIndex, 10);
        Array.from(sel.options).forEach(function (opt) {
          const hypothetical = self._selectedOptions.slice();
          hypothetical[groupIndex] = opt.value;
          const available = self._product.variants.some(function (v) {
            return (
              v.available &&
              v.options.every(function (val, i) {
                return val === hypothetical[i];
              })
            );
          });
          opt.disabled = !available;
        });
      });
    }

    /* ── Render regular / subscribe-discounted price ── */
    _updatePriceDisplay() {
      var price = this._currentPrice;

      // Fall back to whatever Liquid rendered as the regular price
      if (price === null && this._priceEl) {
        price = null; // keep existing text, just toggle visibility
      }

      if (this._isSubscribed && price !== null) {
        var discounted = Math.round(price * 0.6); // 40% off
        if (this._priceSaleEl) {
          this._priceSaleEl.textContent = formatMoney(discounted);
          this._priceSaleEl.hidden = false;
        }
        if (this._priceCompareEl) {
          this._priceCompareEl.textContent = formatMoney(price);
          this._priceCompareEl.hidden = false;
        }
        if (this._priceEl) this._priceEl.hidden = true;
      } else {
        // One-time purchase: discount multiplier from data-otp-multiplier attr (default 0.7 = 30% off)
        if (price !== null) {
          var otMultiplier = parseFloat(this.dataset.otpMultiplier || "0.7");
          var otDiscounted = Math.round(price * otMultiplier);
          if (this._priceSaleEl) {
            this._priceSaleEl.textContent = formatMoney(otDiscounted);
            this._priceSaleEl.hidden = false;
          }
          if (this._priceCompareEl) {
            this._priceCompareEl.textContent = formatMoney(price);
            this._priceCompareEl.hidden = false;
          }
          if (this._priceEl) this._priceEl.hidden = true;
        } else {
          if (this._priceSaleEl) this._priceSaleEl.hidden = true;
          if (this._priceCompareEl) this._priceCompareEl.hidden = true;
          if (this._priceEl) this._priceEl.hidden = false;
        }
      }
    }

    /* ── Subscription / one-time radio ── */
    _onPlanRadioChange(radio) {
      const isSubscribe = radio.value === "subscribe";
      this._isSubscribed = isSubscribe;
      if (this._subscriptionDetails) {
        this._subscriptionDetails.hidden = !isSubscribe;
      }
      if (this._sellingPlanSelect) {
        this._sellingPlanSelect.disabled = !isSubscribe;
        // When one-time: remove the name attribute so it is not submitted
        if (isSubscribe) {
          this._sellingPlanSelect.name = "selling_plan";
        } else {
          this._sellingPlanSelect.name = "";
        }
      }
      this._updatePriceDisplay();
    }

    /* ── Form submit → /cart/add.js ── */
    _onFormSubmit() {
      if (this._atcBtn && this._atcBtn.disabled) return;

      const self = this;
      this._setLoading(true);
      this._clearError();

      // Build minimal payload: only params Shopify /cart/add.js accepts (extra fields can cause 502)
      var variantId = this._variantInput ? this._variantInput.value : "";
      var quantity = 1;
      var qtyInput = this._form.querySelector('input[name="quantity"]');
      if (qtyInput && qtyInput.value)
        quantity = parseInt(qtyInput.value, 10) || 1;

      var formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", String(quantity));
      if (
        this._isSubscribed &&
        this._sellingPlanSelect &&
        this._sellingPlanSelect.value
      ) {
        formData.append("selling_plan", this._sellingPlanSelect.value);
      }
      formData.append("sections", "cart-icon-bubble,cart-drawer");
      formData.append("sections_url", window.location.pathname);

      const headers = { "X-Requested-With": "XMLHttpRequest" };
      // Shopify returns JSON only for /cart/add.js; Liquid routes.cart_add_url may be /cart/add
      var baseUrl =
        (window.routes && window.routes.cart_add_url) || "/cart/add";
      var path = baseUrl.split("?")[0];
      var query =
        baseUrl.indexOf("?") >= 0
          ? "?" + baseUrl.split("?").slice(1).join("?")
          : "";
      var url = (path.endsWith(".js") ? path : path + ".js") + query;

      fetch(url, {
        method: "POST",
        headers: headers,
        body: formData,
        credentials: "same-origin",
      })
        .then(function (res) {
          return res.text().then(function (text) {
            var data;
            try {
              data = text ? JSON.parse(text) : {};
            } catch (e) {
              console.error(
                "[quick-add-soul] Non-JSON response:",
                text ? text.slice(0, 200) : res.status,
              );
              self._showError("Invalid response from store. Please try again.");
              return null;
            }
            return { ok: res.ok, status: res.status, data: data };
          });
        })
        .then(function (result) {
          if (!result) return;

          var data = result.data;

          // Shopify API error (e.g. 422 selling plan not available, sold out, etc.)
          if (!result.ok) {
            var msg = data && (data.description || data.message);
            if (!msg && result.status >= 500 && result.status <= 599) {
              msg =
                "The store is temporarily unavailable. Please try again in a moment.";
            }
            if (!msg) {
              msg = "Could not add to cart (HTTP " + result.status + ").";
            }
            self._showError(msg);
            return;
          }

          // Success — update cart icon bubble first
          var bubbleSection =
            data.sections && data.sections["cart-icon-bubble"];
          if (bubbleSection) {
            var bubbleEl = document.getElementById("cart-icon-bubble");
            if (bubbleEl) {
              try {
                var doc = new DOMParser().parseFromString(
                  bubbleSection,
                  "text/html",
                );
                var inner = doc.querySelector(".shopify-section");
                if (inner) bubbleEl.innerHTML = inner.innerHTML;
              } catch (e) {
                /* non-critical */
              }
            }
          }

          // Show "Added to Cart" feedback on the button
          self._showAddedFeedback();

          // Publish cart update immediately so cart state is fresh
          var suppressDrawer = self.dataset.suppressCartDrawer === "true";
          var openDrawer = function () {
            if (!suppressDrawer && typeof openCartDrawer === "function") {
              openCartDrawer();
            }
          };
          if (
            typeof publish === "function" &&
            typeof PUB_SUB_EVENTS !== "undefined" &&
            PUB_SUB_EVENTS.cartUpdate
          ) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "quick-add-soul",
              cartData: data,
            }).then(openDrawer, openDrawer);
          } else {
            openDrawer();
          }

          // After a short delay, close the popup and hand off to cart-added-popup
          setTimeout(function () {
            self.close();

            if (suppressDrawer) {
              // Silently refresh the drawer HTML so it's up-to-date for when the
              // user manually opens the cart — but do NOT call renderContents()
              // because that always triggers cart-drawer.open().
              try {
                var drawerSection =
                  data.sections && data.sections["cart-drawer"];
                if (drawerSection) {
                  var cartDrawerDiv = document.querySelector("#CartDrawer");
                  var freshHtml = new DOMParser().parseFromString(
                    drawerSection,
                    "text/html",
                  );
                  var freshCartDrawerDiv =
                    freshHtml.querySelector("#CartDrawer");
                  if (cartDrawerDiv && freshCartDrawerDiv) {
                    cartDrawerDiv.innerHTML = freshCartDrawerDiv.innerHTML;
                    var overlay = document.querySelector("#CartDrawer-Overlay");
                    var cartDrawerEl = document.querySelector("cart-drawer");
                    if (overlay && cartDrawerEl) {
                      overlay.addEventListener(
                        "click",
                        cartDrawerEl.close.bind(cartDrawerEl),
                      );
                    }
                  }
                }
              } catch (e) {
                /* non-critical */
              }
            } else {
              try {
                var confirmPopup = document.querySelector("cart-added-popup");
                if (
                  confirmPopup &&
                  typeof confirmPopup.renderContents === "function"
                ) {
                  confirmPopup.renderContents(data);
                }
              } catch (e) {
                // Confirmation popup display failed but the item was added — don't show error
                console.error("[quick-add-soul] cart-added-popup error:", e);
              }
            }
          }, 1500);
        })
        .catch(function (err) {
          // Network failure (CORS, offline, blocked, etc.)
          console.error("[quick-add-soul]", err);
          self._showError("Could not connect. Please try again.");
        })
        .finally(function () {
          self._setLoading(false);
        });
    }

    /* ── Added-to-cart success feedback ── */
    _showAddedFeedback() {
      if (this._atcLabel) {
        this._atcLabel.style.opacity = "";
        this._atcLabel.textContent = "Added to Cart!";
      }
      if (this._atcBtn) {
        this._atcBtn.disabled = true;
        this._atcBtn.classList.add("soul-quick-add__atc-btn--added");
      }
      if (this._atcSpinner) {
        this._atcSpinner.hidden = true;
      }
    }

    /* ── Reset button back to its default add-to-cart label ── */
    _resetAtcButton() {
      if (this._atcBtn) {
        this._atcBtn.classList.remove("soul-quick-add__atc-btn--added");
        // Re-check availability for the current variant
        const variantId = this._variantInput && this._variantInput.value;
        const variant =
          variantId &&
          this._product &&
          this._product.variants.find(function (v) {
            return String(v.id) === String(variantId);
          });
        this._atcBtn.disabled = variant ? !variant.available : false;
      }
      if (this._atcLabel) {
        this._atcLabel.style.opacity = "";
        const variantId = this._variantInput && this._variantInput.value;
        const variant =
          variantId &&
          this._product &&
          this._product.variants.find(function (v) {
            return String(v.id) === String(variantId);
          });
        this._atcLabel.textContent =
          variant && !variant.available
            ? (window.variantStrings && window.variantStrings.soldOut) ||
              "Sold out"
            : (window.variantStrings && window.variantStrings.addToCart) ||
              "Add to cart";
      }
      if (this._atcSpinner) {
        this._atcSpinner.hidden = true;
      }
    }

    /* ── Loading state ── */
    _setLoading(isLoading) {
      if (this._atcBtn) {
        this._atcBtn.setAttribute("aria-busy", isLoading ? "true" : "false");
        // Only disable while in-flight
        if (isLoading) {
          this._atcBtn.disabled = true;
        } else {
          // Re-check availability after loading
          const variantId = this._variantInput && this._variantInput.value;
          const variant =
            variantId &&
            this._product &&
            this._product.variants.find(function (v) {
              return String(v.id) === String(variantId);
            });
          this._atcBtn.disabled = variant ? !variant.available : false;
        }
      }
      if (this._atcSpinner) {
        this._atcSpinner.hidden = !isLoading;
        this._atcSpinner.setAttribute("aria-hidden", "true");
      }
      if (this._atcLabel) {
        this._atcLabel.style.opacity = isLoading ? "0" : "";
      }
    }

    /* ── Error ── */
    _showError(msg) {
      if (this._errorEl) {
        this._errorEl.textContent = msg;
        this._errorEl.hidden = false;
      }
    }

    _clearError() {
      if (this._errorEl) {
        this._errorEl.textContent = "";
        this._errorEl.hidden = true;
      }
    }

    /* ── Open / close ── */
    open() {
      // Ensure connectedCallback has run (guard for late upgrades)
      if (!this._dialog) {
        this._dialog = this.querySelector(".soul-quick-add__dialog");
      }
      if (!this._keydownHandler) {
        const self = this;
        this._keydownHandler = function (e) {
          if (e.key === "Escape") self.close();
        };
      }

      // Move to body so fixed overlay covers the full viewport (no transform/overflow ancestor)
      this._originalParent = this.parentNode;
      this._originalNextSibling = this.nextSibling;
      if (this._originalParent && this._originalParent !== document.body) {
        document.body.appendChild(this);
      }

      // Reset button to its default state in case it was left in "Added to Cart" state
      this._resetAtcButton();

      this.setAttribute("open", "");
      if (this._dialog) {
        this._dialog.removeAttribute("hidden");
        this._dialog.focus();
      }
      document.addEventListener("keydown", this._keydownHandler);
      // Compensate for scrollbar disappearing to prevent layout shift
      var scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight =
        scrollbarWidth > 0 ? scrollbarWidth + "px" : "";
      document.body.style.overflow = "hidden";
    }

    close() {
      this.removeAttribute("open");
      if (this._dialog) {
        this._dialog.setAttribute("hidden", "");
      }
      document.removeEventListener("keydown", this._keydownHandler);
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      this._clearError();

      // Move back to original position in the DOM
      if (this._originalParent && this._originalParent !== document.body) {
        if (this._originalNextSibling) {
          this._originalParent.insertBefore(this, this._originalNextSibling);
        } else {
          this._originalParent.appendChild(this);
        }
        this._originalParent = null;
        this._originalNextSibling = null;
      }

      // Return focus to the trigger button that opened this popup
      if (this._triggerBtn) {
        this._triggerBtn.focus();
        this._triggerBtn = null;
      }
    }
  }

  customElements.define("quick-add-soul", QuickAddSoul);

  /* ─── Trigger buttons on product cards ─── */
  // PDF Issue 9 — "Complete Your Routine" ATC was slow / silent on mobile.
  // Root cause: on slow devices the custom element `quick-add-soul` hadn't been
  // upgraded by the time the user tapped the ATC button. The previous code did a
  // single `requestAnimationFrame` retry and gave up — silently. The user saw
  // either nothing happen, or a long delay (when the upgrade finally landed).
  // Fix: (1) immediate visual feedback so the tap is acknowledged, (2) robust
  // wait via `customElements.whenDefined` + ~600ms polling fallback for late
  // upgrades, (3) re-enable the button if the popup never resolves.
  document.addEventListener("click", function (e) {
    const btn = e.target.closest("[data-quick-add-soul]");
    if (!btn) return;

    const productId = btn.dataset.quickAddSoul;
    if (!productId) return;

    const popup = document.getElementById("QuickAddSoul-" + productId);
    if (!popup) return;

    // (1) Immediate visual feedback — acknowledge the tap right away so users
    // on slow mobile don't double-tap or assume the button is broken.
    if (btn._quickAddArmed) return; // ignore re-taps while we're opening
    btn._quickAddArmed = true;
    btn.setAttribute("aria-busy", "true");

    // Store trigger so we can return focus on close
    popup._triggerBtn = btn;

    function releaseBtn() {
      btn.removeAttribute("aria-busy");
      btn._quickAddArmed = false;
    }

    function tryOpen() {
      if (typeof popup.open === "function") {
        popup.open();
        releaseBtn();
        return true;
      }
      return false;
    }

    // Fast path: already upgraded.
    if (tryOpen()) return;

    // Slow path: wait for the custom element to be defined, with a 600ms ceiling.
    let resolved = false;
    const startedAt = Date.now();

    const finish = function () {
      if (resolved) return;
      resolved = true;
      if (!tryOpen()) releaseBtn(); // give up gracefully so the button isn't stuck busy
    };

    if (
      window.customElements &&
      typeof customElements.whenDefined === "function"
    ) {
      const p = customElements.whenDefined("quick-add-soul");
      if (p && typeof p.then === "function") {
        p.then(finish, finish);
      }
    }

    // Poll every animation frame for up to ~600ms as a fallback. Older browsers
    // without whenDefined, or upgrades that race with our handler, still resolve.
    (function poll() {
      if (resolved) return;
      if (tryOpen()) {
        resolved = true;
        return;
      }
      if (Date.now() - startedAt > 600) {
        finish();
        return;
      }
      requestAnimationFrame(poll);
    })();
  });
})();
